import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getNotificationPrefs,
  updateNotificationPrefs,
  DEFAULT_NOTIFICATION_PREFS,
  resolveAssetUrl,
} from '../services/apiService';
import { useDarkMode } from '../context/DarkModeContext';
import { useLanguage } from '../context/LanguageContext';
import { i18n } from '../i18n/i18n';
import { useAppContext } from '../context/AppContext';
import { supported_languages } from '../i18n/translations';
import { KidIcon } from './kid/KidIcon';
import { KidSwitch, PictureTile } from './kid/KidUI';
import { showToast } from './kid/Toast';
import ConfirmDialog from './kid/ConfirmDialog';
import MySettings from './kid/MySettings';
import StatsVisibilitySetting from './stats/StatsVisibilitySetting';
import AdultApprovalSetting from './settings/AdultApprovalSetting';
import { useDevicePush } from '../hooks/useDevicePush';
import { avatarOrDefault } from '../utils/avatars';
import { FEATURES } from '../constants/features';
import { deleteFamily } from '../services/apiService';
import { familyNameMatches, formatDeletionDate, permanentDateFromNow } from '../utils/accountDeletion';

/**
 * Settings (kid-first redesign step 6c — mock-up phone-15): grouped cards. You (profile row),
 * Language + Look, "Tell me when" (notification switches, saved as you tap), the family's stats
 * choice (grown-ups), and "This device" with Log out as a plain row.
 * A child who lands here gets their own simple choices (the same as on My page).
 */

type Prefs = Record<string, any>;

export const SettingsGroup: React.FC<{ title?: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="space-y-2">
    {title && <h2 className="font-kid-heading font-extrabold text-xl px-1">{title}</h2>}
    <div className="rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark bg-kid-card dark:bg-kid-card-dark divide-y-2 divide-kid-border dark:divide-kid-border-dark overflow-hidden">
      {children}
    </div>
  </section>
);

export const settingsRowClass = 'w-full min-h-[60px] px-4 py-3 flex items-center gap-3 text-left font-bold text-lg';
const rowClass = settingsRowClass;
const Group = SettingsGroup;

const Settings: React.FC = () => {
  const { isDarkMode, setDarkMode, isSystemPreference, setSystemPreference } = useDarkMode();
  const { current_language, set_language, use_system_language, set_use_system_language } = useLanguage();
  const { deviceLogout, refreshDeviceSession, current_user, current_family, switchUser, refreshUserData } = useAppContext() as any;
  const navigate = useNavigate();
  const [prefs, setPrefs] = React.useState<Prefs | null>(null);
  const [confirmLogout, setConfirmLogout] = React.useState(false);
  const [confirmDeleteFamily, setConfirmDeleteFamily] = React.useState(false);
  const [typedFamilyName, setTypedFamilyName] = React.useState('');
  const { device, busy: deviceBusy, turnOn, turnOff } = useDevicePush();
  const isChild = current_user?.member_type === 'child' && !current_user?.is_admin;

  React.useEffect(() => {
    if (isChild) return;
    getNotificationPrefs()
      .then((p) => setPrefs(p || { ...DEFAULT_NOTIFICATION_PREFS }))
      .catch(() => setPrefs({ ...DEFAULT_NOTIFICATION_PREFS }));
  }, [isChild]);

  if (!current_user) return null;

  const header = (
    <div className="flex items-center gap-3 mb-5">
      <button
        type="button"
        onClick={() => navigate('/home')}
        aria-label={i18n.t('settings.back')}
        className="w-12 h-12 rounded-full bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border dark:border-kid-border-dark text-2xl font-bold flex items-center justify-center shrink-0"
      >
        <span aria-hidden>←</span>
      </button>
      <h1 className="font-kid-heading font-extrabold text-3xl md:text-4xl">{i18n.t('settings.title')}</h1>
    </div>
  );

  const page = (children: React.ReactNode) => (
    <div className="w-full max-w-2xl mx-auto px-4 md:px-8 py-4 md:py-7 text-kid-text dark:text-kid-text-dark" lang={current_language}>
      {header}
      {children}
    </div>
  );

  if (isChild) return page(<MySettings />);

  // Saved as you tap; put back if the save fails.
  const savePref = async (patch: Prefs) => {
    if (!prefs) return;
    const before = prefs;
    const next = { ...prefs, ...patch };
    setPrefs(next);
    try {
      await updateNotificationPrefs(next);
    } catch (e: any) {
      setPrefs(before);
      showToast({ message: e?.message || 'Error', tone: 'error' });
    }
  };

  const toggleRow = (key: string, label: string) => (
    <div key={key} className={rowClass}>
      <span className="flex-1">{label}</span>
      <KidSwitch label={label} checked={!!prefs?.[key]} disabled={!prefs} onChange={(v) => savePref({ [key]: v })} />
    </div>
  );

  const languages = [...supported_languages].sort((a, b) => a.label.localeCompare(b.label));
  const look: 'light' | 'dark' | 'auto' = isSystemPreference ? 'auto' : isDarkMode ? 'dark' : 'light';
  const lookButton = (value: 'light' | 'dark' | 'auto', label: string, onPick: () => void) => (
    <button
      type="button"
      role="radio"
      aria-checked={look === value}
      onClick={onPick}
      className={`min-h-[44px] px-4 rounded-full font-bold text-base ${look === value ? 'bg-kid-green text-white' : ''}`}
    >
      {label}
    </button>
  );

  const digestHour = Math.min(21, Math.max(7, prefs?.digest_hour_local ?? 7));
  const timeInput = 'min-h-[44px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark px-2 text-base font-bold';
  const lockedUsernames = (() => {
    const raw = Array.isArray((window as any).__ds_username_locks) ? (window as any).__ds_username_locks : [];
    return raw.filter((r: any) => r.lock_until && new Date(r.lock_until) > new Date());
  })();

  return page(
    <div className="space-y-6">
      {/* you */}
      <Group>
        <Link to="/profile" className={rowClass}>
          <PictureTile src={resolveAssetUrl(avatarOrDefault(current_user.avatar_url))} label={current_user.name} size={48} rounded="rounded-full" />
          <span className="flex-1 min-w-0">
            <span className="block text-xl">{current_user.name}</span>
            <span className="block text-sm font-normal">
              {current_user.is_admin ? i18n.t('settings.profile_parent') : i18n.t('settings.profile_member')}
            </span>
          </span>
          <span aria-hidden className="text-xl">›</span>
        </Link>
      </Group>

      {/* language + look */}
      <Group>
        <label className={`${rowClass} relative cursor-pointer`}>
          <span className="flex-1">{i18n.t('settings.language_section')}</span>
          <span className="font-normal text-base">
            {use_system_language ? i18n.t('my_settings.same_as_device') : supported_languages.find((l) => l.code === current_language)?.label}
          </span>
          <span aria-hidden className="text-xl">›</span>
          <select
            aria-label={i18n.t('settings.language_section')}
            value={use_system_language ? '__device' : current_language}
            onChange={(e) => {
              if (e.target.value === '__device') set_use_system_language(true);
              else { set_use_system_language(false); set_language(e.target.value as any); }
            }}
            className="absolute inset-0 opacity-0 cursor-pointer"
          >
            <option value="__device">{i18n.t('my_settings.same_as_device')}</option>
            {languages.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </label>
        <div className={`${rowClass} flex-wrap`}>
          <span className="flex-1">{i18n.t('settings.look')}</span>
          <div role="radiogroup" aria-label={i18n.t('settings.look')} className="flex rounded-full bg-kid-chip dark:bg-kid-chip-dark p-1">
            {lookButton('light', i18n.t('my_settings.light'), () => setDarkMode(false))}
            {lookButton('dark', i18n.t('my_settings.dark'), () => setDarkMode(true))}
            {lookButton('auto', i18n.t('settings.auto'), () => setSystemPreference(true))}
          </div>
        </div>
      </Group>

      {/* tell me when */}
      <Group title={i18n.t('settings.tell_me_when')}>
        <div className={rowClass}>
          <span className="flex-1">
            {i18n.t('settings.notif_this_device')}
            {(device === 'blocked' || device === 'unsupported') && (
              <span className="block text-sm font-normal">
                {device === 'blocked' ? i18n.t('settings.notif_blocked_parent') : i18n.t('my_settings.notif_unsupported')}
              </span>
            )}
          </span>
          {(device === 'on' || device === 'off') && (
            <KidSwitch
              label={i18n.t('settings.notif_this_device')}
              checked={device === 'on'}
              disabled={deviceBusy}
              onChange={(v) => (v ? turnOn() : turnOff())}
            />
          )}
        </div>
        {current_user.is_admin && toggleRow('task_review_ready_enabled', i18n.t('settings.n_review_ready'))}
        {current_user.is_admin && toggleRow('reward_claimed_enabled', i18n.t('settings.n_reward_claimed'))}
        {toggleRow('task_approved_enabled', i18n.t('settings.n_task_approved'))}
        {toggleRow('reward_claimable_enabled', i18n.t('settings.n_reward_ready'))}
        {toggleRow('task_due_soon_enabled', i18n.t('settings.n_due_soon'))}
        {toggleRow('task_overdue_enabled', i18n.t('settings.n_overdue'))}
        {toggleRow('new_task_available_enabled', i18n.t('settings.n_new_task'))}
        {toggleRow('event_alerts_enabled', i18n.t('settings.n_event'))}
        <div className={`${rowClass} flex-wrap`}>
          <span className="flex-1">{i18n.t('settings.daily_summary')}</span>
          {/* Scheduled pushes only run 07–21 (Danish time), so the hour is kept in that range. */}
          <select
            aria-label={i18n.t('settings.daily_summary_time')}
            value={digestHour}
            disabled={!prefs || !prefs.daily_digest_enabled}
            onChange={(e) => savePref({ digest_hour_local: Number(e.target.value) })}
            className={`${timeInput} disabled:opacity-50`}
          >
            {Array.from({ length: 15 }, (_, i) => i + 7).map((h) => (
              <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
            ))}
          </select>
          <KidSwitch label={i18n.t('settings.daily_summary')} checked={!!prefs?.daily_digest_enabled} disabled={!prefs}
            onChange={(v) => savePref({ daily_digest_enabled: v })} />
        </div>
        <div className={`${rowClass} flex-wrap`}>
          <span className="flex-1 min-w-[10rem]">
            {i18n.t('settings.quiet_hours')}
            <span className="block text-sm font-normal">{i18n.t('settings.quiet_hours_hint')}</span>
          </span>
          <span className="flex items-center gap-2">
            <input type="time" aria-label={i18n.t('settings.quiet_from')} value={prefs?.quiet_hours_start || ''} disabled={!prefs}
              onChange={(e) => savePref({ quiet_hours_start: e.target.value })} className={timeInput} />
            <span aria-hidden>–</span>
            <input type="time" aria-label={i18n.t('settings.quiet_to')} value={prefs?.quiet_hours_end || ''} disabled={!prefs}
              onChange={(e) => savePref({ quiet_hours_end: e.target.value })} className={timeInput} />
          </span>
        </div>
      </Group>

      {current_user.is_admin && <AdultApprovalSetting />}

      {current_user.is_admin && <StatsVisibilitySetting />}

      {/* payments (behind FEATURES.payments) */}
      {FEATURES.payments && current_user.is_admin && (
        <Group>
          <Link to="/subscription" className={rowClass}>
            <span aria-hidden className="text-xl w-6 text-center">💳</span>
            <span className="flex-1">{i18n.t('billing.title')}</span>
            <span aria-hidden className="text-xl">›</span>
          </Link>
        </Group>
      )}

      {/* this device */}
      <Group title={i18n.t('settings.this_device')}>
        <Link to="/profile" className={rowClass}>
          <KidIcon name="lock" size={24} className="shrink-0" />
          <span className="flex-1">{i18n.t('settings.change_pin')}</span>
          <span aria-hidden className="text-xl">›</span>
        </Link>
        <button type="button" onClick={() => switchUser()} className={rowClass}>
          <span aria-hidden className="text-xl w-6 text-center">👥</span>
          <span className="flex-1">{i18n.t('settings.switch_person')}</span>
          <span aria-hidden className="text-xl">›</span>
        </button>
        <button
          type="button"
          onClick={async () => {
            try {
              await refreshDeviceSession?.();
              showToast({ message: i18n.t('settings.session_refreshed') });
            } catch (e: any) {
              showToast({ message: e?.message || 'Error', tone: 'error' });
            }
          }}
          className={rowClass}
        >
          <span aria-hidden className="text-xl w-6 text-center">↻</span>
          <span className="flex-1">{i18n.t('settings.refresh_session')}</span>
        </button>
        {current_user.is_admin && lockedUsernames.map((r: any) => (
          <div key={r.username} className={rowClass}>
            <span className="flex-1">
              @{r.username}
              <span className="block text-sm font-normal">
                {i18n.t('settings.locked_until').replace('{{time}}', new Date(r.lock_until).toLocaleTimeString(current_language, { hour: '2-digit', minute: '2-digit' }))}
              </span>
            </span>
            <UnlockUsernameButton username={r.username} />
          </div>
        ))}
        <button type="button" onClick={() => setConfirmLogout(true)} className={rowClass}>
          <span aria-hidden className="text-xl w-6 text-center">⇥</span>
          <span className="flex-1">{i18n.t('settings.log_out_device')}</span>
        </button>
      </Group>

      {/* delete the family (admins) — marked first, can be restored for 30 days */}
      {current_user.is_admin && (
        <Group title={i18n.t('deletion.family_section')}>
          <button
            type="button"
            onClick={() => { setTypedFamilyName(''); setConfirmDeleteFamily(true); }}
            className={`${rowClass} text-red-700 dark:text-red-300`}
          >
            <span aria-hidden className="text-xl w-6 text-center">🗑</span>
            <span className="flex-1">
              {i18n.t('deletion.delete_family_row')}
              <span className="block text-sm font-normal text-kid-text dark:text-kid-text-dark">{i18n.t('deletion.delete_family_hint')}</span>
            </span>
          </button>
        </Group>
      )}

      <ConfirmDialog
        open={confirmDeleteFamily}
        title={i18n.t('deletion.delete_family_title')}
        message={
          <div className="space-y-3">
            <p>{i18n.t('deletion.delete_family_text').replace('{{family}}', current_family?.name || '')}</p>
            <p className="font-bold">{i18n.t('deletion.delete_family_lost')}</p>
            <p>{i18n.t('deletion.undo_30').replace('{{date}}', formatDeletionDate(permanentDateFromNow(), current_language))}</p>
            <label className="block">
              <span className="block font-bold mb-1">
                {i18n.t('deletion.type_family_name').replace('{{family}}', current_family?.name || '')}
              </span>
              <input
                type="text"
                value={typedFamilyName}
                onChange={(e) => setTypedFamilyName(e.target.value)}
                autoComplete="off"
                className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark px-3 text-base"
              />
            </label>
          </div>
        }
        confirmLabel={i18n.t('deletion.delete_family_yes')}
        cancelLabel={i18n.t('deletion.cancel')}
        onCancel={() => setConfirmDeleteFamily(false)}
        onConfirm={async () => {
          if (!familyNameMatches(typedFamilyName, current_family?.name || '')) {
            throw new Error(i18n.t('deletion.type_family_name').replace('{{family}}', current_family?.name || ''));
          }
          await deleteFamily(typedFamilyName);
          setConfirmDeleteFamily(false);
          await refreshUserData();
        }}
      />

      <ConfirmDialog
        open={confirmLogout}
        title={i18n.t('settings.logout_confirm_title')}
        message={i18n.t('settings.logout_confirm_text').replace('{{family}}', current_family?.name || '')}
        confirmLabel={i18n.t('settings.logout_confirm_yes')}
        cancelLabel={i18n.t('settings.cancel')}
        onCancel={() => setConfirmLogout(false)}
        onConfirm={async () => { await deviceLogout(); setConfirmLogout(false); }}
      />
    </div>,
  );
};

export default Settings;

const UnlockUsernameButton: React.FC<{ username: string }> = ({ username }) => {
  const { refreshDeviceSession } = useAppContext() as any;
  return (
    <button
      type="button"
      onClick={async () => {
        const { deviceUnlockUsername } = await import('../services/apiService');
        await deviceUnlockUsername(username);
        await refreshDeviceSession?.();
        // Trim the just-unlocked entry immediately without waiting for next fetch
        const raw = Array.isArray((window as any).__ds_username_locks) ? (window as any).__ds_username_locks : [];
        (window as any).__ds_username_locks = raw.filter((r: any) => r.username !== username);
        showToast({ message: i18n.t('settings.unlocked').replace('{{name}}', username) });
      }}
      className="min-h-[44px] px-4 rounded-full border-2 border-kid-border-strong dark:border-kid-border-strong-dark font-bold text-base"
    >
      {i18n.t('settings.unlock')}
    </button>
  );
};
